import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Upload, FileText, Lock, X, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminAmazingReadsPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [pdfs, setPdfs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [tags, setTags] = useState('');

  useEffect(() => {
    const authStatus = sessionStorage.getItem('admin_auth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      fetchPDFs();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      console.log('Attempting login with API:', API);
      const response = await fetch(`${API}/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (response.ok && data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_auth', 'true');
        toast({
          title: 'Access Granted',
          description: 'Welcome to Amazing Reads admin'
        });
        fetchPDFs();
      } else {
        toast({
          title: 'Access Denied',
          description: 'Incorrect password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Error',
        description: `Unable to verify credentials: ${error.message}`,
        variant: 'destructive'
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    setPassword('');
    toast({
      title: 'Logged Out',
      description: 'You have been logged out'
    });
  };

  const fetchPDFs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/amazing-reads`);
      setPdfs(response.data);
    } catch (error) {
      console.error('Error fetching PDFs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load PDFs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid File',
        description: 'Only PDF files are allowed',
        variant: 'destructive'
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: 'Title Required',
        description: 'Please enter a title for the PDF',
        variant: 'destructive'
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('author', author);
    formData.append('tags', tags);

    try {
      setUploading(true);
      await axios.post(`${API}/amazing-reads/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast({
        title: 'Success',
        description: `"${title}" uploaded successfully`
      });
      
      // Reset form
      setTitle('');
      setDescription('');
      setAuthor('');
      setTags('');
      event.target.value = '';
      
      fetchPDFs();
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload PDF',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (pdfId, pdfTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${pdfTitle}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/amazing-reads/${pdfId}`);
      toast({
        title: 'Deleted',
        description: `"${pdfTitle}" has been deleted`
      });
      fetchPDFs();
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete PDF',
        variant: 'destructive'
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F1A2A] via-[#1A1A1C] to-[#0F1A2A] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#C9A64A]/20 border border-[#C9A64A]/30 mb-4">
              <Lock className="h-8 w-8 text-[#C9A64A]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Access</h1>
            <p className="text-gray-400">Enter password to manage resources</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10">
            <div className="mb-6">
              <label className="block text-[#C9A64A] text-xs font-bold mb-2 uppercase tracking-widest">
                Password
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Enter admin password"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-bold"
              disabled={loginLoading}
            >
              {loginLoading ? 'Verifying...' : 'Login'}
            </Button>

            <Link to="/" className="block text-center mt-4 text-gray-400 hover:text-[#C9A64A] transition-colors text-sm">
              Back to Home
            </Link>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-7 w-7 text-[#C9A64A]" />
              <BarChart3 className="h-4 w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-xl font-bold text-[#1A1A1C] tracking-tight">
              Virtual<span className="text-[#C9A64A]">CFO</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#5A6270]">Admin Panel</span>
            <Button onClick={handleLogout} variant="outline" size="sm">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-[#1A1A1C] mb-2">Amazing Reads Admin</h1>
            <p className="text-[#5A6270]">Upload and manage PDF resources for the Amazing Reads section</p>
          </div>

          {/* Upload Section */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#1A1A1C] mb-6">Upload New PDF</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1C] mb-2">
                  Title *
                </label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., PostHog: From Pivot Hell to Unicorn"
                  className="border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1A1A1C] mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the PDF content..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A64A] focus:border-[#C9A64A] outline-none"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1C] mb-2">
                    Author
                  </label>
                  <Input
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="e.g., Y Combinator"
                    className="border-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1C] mb-2">
                    Tags (comma-separated)
                  </label>
                  <Input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="e.g., Startup, Product, SaaS"
                    className="border-gray-300"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex-1 cursor-pointer">
                <div className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] rounded-xl hover:scale-105 transition-all duration-300 font-semibold">
                  <Upload className="h-5 w-5" />
                  {uploading ? 'Uploading...' : 'Select PDF File'}
                </div>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,application/pdf"
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>

          {/* PDF List */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-[#1A1A1C] mb-6">Uploaded PDFs ({pdfs.length})</h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#C9A64A] border-t-transparent"></div>
                <p className="text-[#5A6270] mt-4">Loading PDFs...</p>
              </div>
            ) : pdfs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-[#5A6270]">No PDFs uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pdfs.map((pdf) => (
                  <div key={pdf.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-xl hover:border-[#C9A64A] transition-colors">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-red-500" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-[#1A1A1C] mb-1">{pdf.title}</h3>
                      {pdf.description && (
                        <p className="text-sm text-[#5A6270] mb-2">{pdf.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 text-xs text-[#5A6270]">
                        <span>{formatFileSize(pdf.file_size)}</span>
                        <span>•</span>
                        <span>{formatDate(pdf.upload_date)}</span>
                        <span>•</span>
                        <span>{pdf.view_count} views</span>
                        {pdf.author && (
                          <>
                            <span>•</span>
                            <span>By {pdf.author}</span>
                          </>
                        )}
                      </div>
                      {pdf.tags && pdf.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {pdf.tags.map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-[#C9A64A]/10 text-[#C9A64A] text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => handleDelete(pdf.id, pdf.title)}
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
